const API_PREFIX = '/api';

let _activeConnectionId = null;
const _lastCredentialsByConnectionId = new Map();
const _reconnectingByConnectionId = new Map();

const normalizeConnectionId = (value) => {
  const connectionId = String(value || '').trim();
  return connectionId || null;
};

const getConnectionHeaders = (connectionId) =>
  connectionId ?
    { 'X-Kinetic-Connection-Id': connectionId } :
    {};

const parseApiError = async (response) => {
  try {
    const data = await response.json();
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  } catch {
    // Ignore parse failure and use fallback.
  }
  return `${response.status} ${response.statusText}`;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const getRetryDelay = (response) => {
  const retryAfter = response.headers.get('Retry-After');
  if (retryAfter) {
    const seconds = Number(retryAfter);
    if (!isNaN(seconds) && seconds > 0) return seconds * 1000;
  }
  return 5000;
};

const autoReconnect = async (connectionId = _activeConnectionId) => {
  const normalizedId = normalizeConnectionId(connectionId);
  const credentials = normalizedId ? _lastCredentialsByConnectionId.get(normalizedId) : null;
  if (!credentials || !normalizedId) return false;
  const pending = _reconnectingByConnectionId.get(normalizedId);
  if (pending) return pending;
  const reconnectPromise = (async () => {
    try {
      console.log(`[webClient] auto-reconnecting ${normalizedId}...`);
      const res = await fetch(`${API_PREFIX}/session/connect`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.ok) {
          _activeConnectionId = normalizedId;
          console.log(`[webClient] auto-reconnect succeeded for ${normalizedId}`);
          return true;
        }
      }
      console.warn(`[webClient] auto-reconnect failed for ${normalizedId}`);
      return false;
    } catch (err) {
      console.warn(`[webClient] auto-reconnect error for ${normalizedId}:`, err.message);
      return false;
    } finally {
      _reconnectingByConnectionId.delete(normalizedId);
    }
  })();
  _reconnectingByConnectionId.set(normalizedId, reconnectPromise);
  return reconnectPromise;
};

const requestJson = async (path, options = {}, retries = 3, _didReconnect = false, requestConnectionId = normalizeConnectionId(options.connectionId) || _activeConnectionId) => {
  const response = await fetch(`${API_PREFIX}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      ...getConnectionHeaders(requestConnectionId),
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  });

  if (response.status === 429 && retries > 0) {
    const delay = getRetryDelay(response);
    await sleep(delay);
    return requestJson(path, options, retries - 1, _didReconnect);
  }

  if (response.status === 401 && !_didReconnect && !path.startsWith('/session/')) {
    const ok = await autoReconnect(requestConnectionId);
    if (ok) return requestJson(path, options, retries, true, requestConnectionId);
  }

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  if (response.status === 204) return null;
  return response.json();
};

const requestBlob = async (path, retries = 3, _didReconnect = false, requestConnectionId = _activeConnectionId) => {
  const response = await fetch(`${API_PREFIX}${path}`, {
    credentials: 'include',
    headers: getConnectionHeaders(requestConnectionId)
  });
  if (response.status === 429 && retries > 0) {
    const delay = getRetryDelay(response);
    await sleep(delay);
    return requestBlob(path, retries - 1, _didReconnect);
  }
  if (response.status === 401 && !_didReconnect) {
    const ok = await autoReconnect(requestConnectionId);
    if (ok) return requestBlob(path, retries, true, requestConnectionId);
  }
  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }
  return response.blob();
};

const uploadBlob = (path, blob, { onProgress, signal, connectionId } = {}) => {
  const body = blob instanceof Blob ? blob : new Blob([blob]);
  const requestConnectionId = normalizeConnectionId(connectionId) || _activeConnectionId;
  const attempt = (retriesLeft, didReconnect = false) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_PREFIX}${path}`);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    if (requestConnectionId) {
      xhr.setRequestHeader('X-Kinetic-Connection-Id', requestConnectionId);
    }
    xhr.withCredentials = true;

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else if (xhr.status === 429 && retriesLeft > 0) {
        const retryAfter = xhr.getResponseHeader('Retry-After');
        const delay = retryAfter && !isNaN(Number(retryAfter)) ? Number(retryAfter) * 1000 : 5000;
        sleep(delay).then(() => attempt(retriesLeft - 1, didReconnect).then(resolve, reject));
      } else if (xhr.status === 401 && !didReconnect) {
        autoReconnect(requestConnectionId).then((ok) => {
          if (ok) attempt(retriesLeft, true).then(resolve, reject);
          else reject(new Error('Session expired. Please reconnect.'));
        });
      } else {
        let msg = `${xhr.status} ${xhr.statusText}`;
        try {
          const data = JSON.parse(xhr.responseText);
          if (data?.error) msg = data.error;
        } catch {
          // Ignore parse failures and use the default message.
        }
        reject(new Error(msg));
      }
    };

    xhr.onerror = () => {
      if (retriesLeft > 0) {
        sleep(2000).then(() => attempt(retriesLeft - 1, didReconnect).then(resolve, reject));
      } else {
        reject(new Error('Upload network error'));
      }
    };
    xhr.onabort = () => reject(new Error('Upload cancelled'));

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(body);
  });

  return attempt(3);
};

export const webClient = {
  async getSessionStatus() {
    return requestJson('/session/status');
  },

  async connect(config, connectionId = 'default') {
    const normalizedId = normalizeConnectionId(connectionId) || 'default';
    const payload = { ...config, connectionId: normalizedId };
    const result = await requestJson('/session/connect', {
      method: 'POST',
      body: JSON.stringify(payload),
      connectionId: normalizedId
    });
    if (result?.ok) {
      _lastCredentialsByConnectionId.set(normalizedId, payload);
      _activeConnectionId = normalizedId;
    }
    return result;
  },

  async disconnect(connectionId = null) {
    const normalizedId = normalizeConnectionId(connectionId);
    if (normalizedId) {
      _lastCredentialsByConnectionId.delete(normalizedId);
    } else {
      _lastCredentialsByConnectionId.clear();
    }
    await requestJson('/session/disconnect', {
      method: 'POST',
      body: JSON.stringify(normalizedId ? { connectionId: normalizedId } : {}),
      connectionId: normalizedId || _activeConnectionId
    });
    if (!normalizedId || _activeConnectionId === normalizedId) {
      _activeConnectionId = null;
    }
  },

  async setActiveConnection(connectionId) {
    const normalizedId = normalizeConnectionId(connectionId);
    await requestJson('/session/setActive', {
      method: 'POST',
      body: JSON.stringify({ connectionId: normalizedId })
    });
    _activeConnectionId = normalizedId;
  },

  async listRemote(remotePath = '/', options = {}) {
    const page = Number.isInteger(options?.page) && options.page > 0 ? options.page : 1;
    const pageSize = Number.isInteger(options?.pageSize) && options.pageSize > 0 ? options.pageSize : undefined;
    const query = new URLSearchParams({ path: remotePath, page: String(page) });
    if (pageSize) query.set('pageSize', String(pageSize));
    return requestJson(`/remote/list?${query.toString()}`);
  },

  async folderSize(remotePath) {
    const result = await requestJson(`/remote/folder-size?path=${encodeURIComponent(remotePath)}`);
    return result.size;
  },

  async readRemoteFile(remotePath) {
    const result = await requestJson(`/remote/file?path=${encodeURIComponent(remotePath)}`);
    return result.content || '';
  },

  async readRemoteFileChunk(remotePath, offset = 0, length = 256 * 1024) {
    return requestJson(
      `/remote/file-range?path=${encodeURIComponent(remotePath)}&offset=${encodeURIComponent(offset)}&length=${encodeURIComponent(length)}`
    );
  },

  async writeRemoteFile(remotePath, content) {
    await requestJson('/remote/file', {
      method: 'PUT',
      body: JSON.stringify({ path: remotePath, content })
    });
  },

  async createRemoteFile(remotePath, content = '') {
    await requestJson('/remote/file', {
      method: 'POST',
      body: JSON.stringify({ path: remotePath, content })
    });
  },

  async createRemoteDir(remotePath) {
    await requestJson('/remote/dir', {
      method: 'POST',
      body: JSON.stringify({ path: remotePath })
    });
  },

  async renameRemote(from, to) {
    await requestJson('/remote/rename', {
      method: 'POST',
      body: JSON.stringify({ from, to })
    });
  },

  async deleteRemote(remotePath) {
    await requestJson(`/remote/item?path=${encodeURIComponent(remotePath)}`, {
      method: 'DELETE'
    });
  },

  async unzipRemote(zipPath, destDir) {
    return requestJson('/remote/unzip', {
      method: 'POST',
      body: JSON.stringify({ path: zipPath, dest: destDir })
    });
  },

  async archiveRemote(paths, dest) {
    return requestJson('/remote/archive', {
      method: 'POST',
      body: JSON.stringify({ paths, dest })
    });
  },

  uploadFile(remotePath, file, { onProgress, signal } = {}) {
    return uploadBlob(`/remote/upload?path=${encodeURIComponent(remotePath)}`, file, {
      onProgress,
      signal,
      connectionId: _activeConnectionId
    });
  },

  async downloadFile(remotePath, { onProgress, signal } = {}, retries = 3, _didReconnect = false, requestConnectionId = _activeConnectionId) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${API_PREFIX}/remote/download?path=${encodeURIComponent(remotePath)}`);
      if (requestConnectionId) {
        xhr.setRequestHeader('X-Kinetic-Connection-Id', requestConnectionId);
      }
      xhr.withCredentials = true;
      xhr.responseType = 'blob';
      if (onProgress) {
        xhr.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        };
      }
      const abortHandler = () => xhr.abort();
      if (signal) signal.addEventListener('abort', abortHandler, { once: true });
      const cleanup = () => { if (signal) signal.removeEventListener('abort', abortHandler); };
      xhr.onload = () => {
        cleanup();
        if (xhr.status >= 200 && xhr.status < 300) { resolve(xhr.response); return; }
        if (xhr.status === 429 && retries > 0) {
          sleep(5000).then(() => this.downloadFile(remotePath, { onProgress, signal }, retries - 1, _didReconnect, requestConnectionId).then(resolve, reject));
          return;
        }
        if (xhr.status === 401 && !_didReconnect) {
          autoReconnect(requestConnectionId).then((ok) => {
            if (ok) this.downloadFile(remotePath, { onProgress, signal }, retries, true, requestConnectionId).then(resolve, reject);
            else reject(new Error('Not authenticated'));
          });
          return;
        }
        xhr.response.text().then((text) => {
          try { reject(new Error(JSON.parse(text).error || `${xhr.status}`)); }
          catch { reject(new Error(`${xhr.status} ${xhr.statusText}`)); }
        }).catch(() => reject(new Error(`${xhr.status} ${xhr.statusText}`)));
      };
      xhr.onerror = () => { cleanup(); reject(new Error('Network error')); };
      xhr.onabort = () => { cleanup(); reject(new Error('Download cancelled')); };
      xhr.send();
    });
  },

  async transferConnect(side, config) {
    return requestJson('/transfer/connect', {
      method: 'POST',
      body: JSON.stringify({ ...config, side })
    });
  },

  async transferList(side, remotePath = '/', options = {}) {
    const page = Number.isInteger(options?.page) && options.page > 0 ? options.page : 1;
    const pageSize = Number.isInteger(options?.pageSize) && options.pageSize > 0 ? options.pageSize : undefined;
    const query = new URLSearchParams({
      side,
      path: remotePath,
      page: String(page)
    });
    if (pageSize) query.set('pageSize', String(pageSize));
    return requestJson(`/transfer/list?${query.toString()}`);
  },

  async transferCopy({ from, to, paths, targetDir, ifExists = 'replace', onProgress }) {
    const started = await requestJson('/transfer/copy', {
      method: 'POST',
      body: JSON.stringify({ from, to, paths, targetDir, ifExists })
    });
    const transferId = started?.transferId;
    const summary = { copied: 0, skipped: 0, failed: 0 };
    // Poll for progress
    let cursor = 0;
    while (true) {
      await sleep(800);
      const query = new URLSearchParams({ cursor: String(cursor) });
      if (transferId) query.set('id', transferId);
      const res = await requestJson(`/transfer/progress?${query.toString()}`);
      if (res.events && res.events.length > 0) {
        for (const event of res.events) {
          if (!event?.isDir) {
            if (event.status === 'ok') summary.copied += 1;
            else if (event.status === 'skipped') summary.skipped += 1;
            else if (event.status === 'error') summary.failed += 1;
          } else if (event.status === 'skipped') {
            summary.skipped += 1;
          }
          if (onProgress) onProgress(event);
        }
      }
      cursor = res.cursor || cursor;
      if (res.done) {
        if (res.error) throw new Error(res.error);
        break;
      }
    }
    return { ok: true, summary };
  }
};
